import React, { useEffect, useState } from "react";
import Footer from "./Footer";
import { DS } from "../../utils/designSystem";

/**
 * Shared layout for Privacy Policy, Cookie Policy, and Terms pages.
 * Props:
 *   pageTitle      – large heading
 *   effectiveDate  – "May 2, 2026"
 *   sections       – [{ id: string, title: string, content: string[] }]
 */
function LegalPageLayout({ pageTitle, effectiveDate, sections }) {
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");

  /* Track active section on scroll */
  useEffect(() => {
    const handler = () => {
      const offset = window.scrollY + 120;
      let current = sections[0]?.id;
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el && el.offsetTop <= offset) current = s.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, [sections]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: el.offsetTop - 88, behavior: "smooth" });
  };

  return (
    <div className="bg-richblack-900">
      {/* ── Hero ─────────────────────────────── */}
      <section className="border-b border-richblack-700 bg-richblack-800">
        <div className={`${DS.layout.container} py-12 lg:py-16`}>
          <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-widest text-richblack-400">
            Legal
          </span>
          <h1 className="text-4xl font-bold text-richblack-5 lg:text-5xl">{pageTitle}</h1>
          <p className="mt-3 text-sm text-richblack-400">
            Effective date: <span className="text-richblack-300">{effectiveDate}</span>
          </p>
        </div>
      </section>

      {/* ── Body ─────────────────────────────── */}
      <section className="py-12">
        <div className={`${DS.layout.container} flex gap-12`}>

          {/* Sticky ToC — desktop only */}
          <aside className="hidden w-52 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-richblack-500">
                Contents
              </p>
              <nav className="flex flex-col gap-0.5">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`rounded-lg px-3 py-2 text-left text-sm transition-colors duration-150 ${
                      activeId === s.id
                        ? "bg-yellow-50/10 font-semibold text-yellow-50"
                        : "text-richblack-400 hover:bg-richblack-800 hover:text-richblack-100"
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="min-w-0 flex-1">
            <div className="flex flex-col gap-10">
              {sections.map((s) => (
                <section key={s.id} id={s.id}>
                  <h2 className="mb-4 border-b border-richblack-700 pb-3 text-xl font-semibold text-richblack-5">
                    {s.title}
                  </h2>
                  <div className="flex flex-col gap-3">
                    {s.content.map((para, i) => (
                      <p key={i} className="text-sm leading-relaxed text-richblack-300">
                        {para}
                      </p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <p className="mt-12 border-t border-richblack-700 pt-6 text-xs text-richblack-500">
              Last updated: {effectiveDate}. Questions?{" "}
              <a
                href="mailto:connect@kodevedic.com"
                className="text-yellow-50 hover:underline"
              >
                connect@kodevedic.com
              </a>
            </p>
          </main>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default LegalPageLayout;
